<!-- insert toast at the top
<newToastVue />

import { toastStore } from "@/store/ToastManagement";

use toast like this:

 this.toast.success({
title: "Succesful upload", subtitle: "uploaded successfully", position:
"Bottom", icon: "forbidden.svg", });

position can be 'Top' or 'Bottom'
success, error and warning are the types
-->

<template>
  <transition name="toast z-[999999]">
    <ul v-if="toast.toasts.length" class="toast-wrapper" :class="positionStyle">
      <li
        v-for="toastItem in toast.toasts"
        :key="toastItem"
        class="toast-list toast-background"
        :class="[wrapperMap[toastItem.theme], getPosition(toastItem.position)]"
      >
        <div class="p-5">
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-3.5">
              <img :src="getIcon(toastItem.icon)" />
              <div class="flex flex-col gap-2">
                <h4
                  :class="[titleMap[toastItem.theme]]"
                  class="font-semibold text-xl leading-6"
                >
                  {{ toastItem.title }}
                </h4>
                <p
                  :class="[subTitleMap[toastItem.theme]]"
                  class="font-normal text-base leading-5"
                >
                  {{ toastItem.subtitle }}
                </p>
              </div>
            </div>

            <img
              :src="getCloseIcon(toastItem.theme)"
              alt="close"
              class="cursor-pointer"
              @click="closeToast(toastItem.id)"
            />
          </div>
          <div
            :class="[trackMap[toastItem.theme]]"
            class="h-1 w-full rounded-[20px] mt-[20px] relative"
          >
            <div
              :style="[getProgressBarStyle(toastItem)]"
              class="h-full rounded-[20px] absolute top-0 left-0"
              :class="[barMap[toastItem.theme]]"
            ></div>
          </div>
        </div>
      </li>
    </ul>
  </transition>
</template>

<script>
import positiveIcon from "../../../assets/svg/close-circle.svg";
import negativeIcon from "../../../assets/svg/circle-close-red.svg";
import warningIcon from "../../../assets/svg/circle-close-yellow.svg";
import { toastStore } from "@/store/ToastManagement";

export default {
  props: {},
  data() {
    return {
      toast: toastStore(),
      positionMap: {
        Top: "absolute top-0",
        Bottom: "absolute bottom-0",
      },
      wrapperMap: {
        warning: "border-inputGrey-300 warning-background bg-inputGrey-400",
        error: " border-[#FADBD7] negative-background bg-[#FFDAD5]",
        success: "border-green-200 positive-background bg-black",
      },
      titleMap: {
        warning: "text-yellow-500",
        error: "text-red-400",
        success: " text-estationsGreen-400",
      },
      subTitleMap: {
        warning: "text-gray-900",
        error: "text-gray-300",
        success: " text-estationsGreen-100",
      },
      trackMap: {
        warning: "bg-yellow-50",
        error: "bg-red-50",
        success: "bg-estationsGreen-100",
      },
      barMap: {
        warning: "bg-yellow-400",
        error: "bg-red-600",
        success: "bg-estationsGreen-400",
      },
      positiveIcon,
      negativeIcon,
      warningIcon,
      progress: 0, // Progress percentage
      intervalId: null, // Store interval ID for progress update
      positionStyle: "",
      currentToast: "",
    };
  },
  watch: {
    "toast.toasts": {
      handler(newToasts) {
        newToasts.forEach((toast) => {
          if (!toast.progressInterval) {
            this.startProgress(toast.id);
          }
        });
      },
      deep: true,
    },
  },
  computed: {},
  mounted() {},
  methods: {
    getProgressBarStyle(toastItem) {
      return {
        width: `${toastItem.progress || 0}%`,
        transition: "width 0.3s linear",
      };
    },
    getPosition(value) {
      const position = this.positionMap[value];
      this.positionStyle = value == "Top" ? "top-0" : "bottom-0";
      return position;
    },
    getIcon(value) {
      return new URL(`../../../assets/svg/${value}`, import.meta.url).href;
    },
    getCloseIcon(value) {
      let Icon;
      if (value == "success") {
        Icon = positiveIcon;
      } else if (value == "error") {
        Icon = negativeIcon;
      } else if (value == "warning") {
        Icon = warningIcon;
      }

      return Icon;
    },
    startProgress(toastId) {
      const toast = this.toast.toasts.find((t) => t.id === toastId);
      if (!toast) return;

      toast.progressInterval = setInterval(() => {
        if (toast.progress >= 100) {
          clearInterval(toast.progressInterval);
          this.toast.removeToast(toastId);
        } else {
          this.toast.updateProgress(toastId, 100 / (3000 / 100));
        }
      }, 100);
    },

    stopProgress() {
      clearInterval(this.intervalId);
    },
    closeToast(toastId) {
      this.toast.removeToast(toastId);
      const toast = this.toast.toasts.find((t) => t.id === toastId);
      if (toast && toast.progressInterval) {
        clearInterval(toast.progressInterval);
      }
    },
  },
};
</script>

<style>
.toast-background {
  background-repeat: no-repeat;
  background-position: -20%, center;
  background-size: 100%;
}

.positive-background {
  background-image: url("../../../assets/svg/texture.svg"),
    url("../../../assets/svg/toastBg.svg");
}

.negative-background {
  background-image: url("../../../assets/svg/negativeTexture.svg"),
    url("../../../assets/svg/negativeToastBg.svg");
}

.warning-background {
  background-image: url("../../../assets/svg/warningToastBg.svg");
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.5s ease-in-out, transform 0.4s ease-in-out;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}
.toast-enter-to {
  opacity: 1;
  transform: translateX(0);
}
.toast-leave-from {
  opacity: 1;
  transform: translateX(0);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

body {
  overflow-x: hidden;
}
.toast-wrapper {
  position: fixed;

  right: 5%;
  z-index: 99999;
  font-size: 14px;
}
.toast-list {
  width: 450px;
  border-width: 2px;
  border-radius: 20px;
  margin-bottom: 10px;
  position: relative;
}
</style>
